import React from 'react';
import { Code, Globe, Network, Database, Shield, Cloud, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const DigitalSolutions = () => {
  const { language } = useLanguage();

  const services = [
    {
      icon: Code,
      title: { fr: 'Logiciels Métiers Sur Mesure', en: 'Custom Business Software' },
      description: { fr: 'Conception et développement de solutions adaptées', en: 'Design and development of tailored solutions' },
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6',
      features: [
        { fr: 'Analyse des besoins métiers', en: 'Business needs analysis' },
        { fr: 'Développement sur mesure', en: 'Custom development' },
        { fr: 'Intégration avec systèmes existants', en: 'Integration with existing systems' },
        { fr: 'Formation utilisateurs', en: 'User training' },
        { fr: 'Support et maintenance', en: 'Support and maintenance' }
      ]
    },
    {
      icon: Globe,
      title: { fr: 'Sites Web & Intranets', en: 'Websites & Intranets' },
      description: { fr: 'Plateformes web professionnelles et intranets institutionnels', en: 'Professional web platforms and institutional intranets' },
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
      features: [
        { fr: 'Sites institutionnels', en: 'Institutional websites' },
        { fr: 'Portails intranets sécurisés', en: 'Secure intranet portals' },
        { fr: 'Design responsive', en: 'Responsive design' },
        { fr: 'CMS et gestion de contenu', en: 'CMS and content management' },
        { fr: 'Hébergement et maintenance', en: 'Hosting and maintenance' }
      ]
    },
    {
      icon: Network,
      title: { fr: 'Intégration Réseaux', en: 'Network Integration' },
      description: { fr: 'Infrastructure LAN, Wi-Fi et réseaux d\'entreprise', en: 'LAN, Wi-Fi and enterprise network infrastructure' },
      image: 'https://images.unsplash.com/photo-1606904825846-647eb07f5be2',
      features: [
        { fr: 'Conception architecture réseau', en: 'Network architecture design' },
        { fr: 'Installation LAN/Wi-Fi', en: 'LAN/Wi-Fi installation' },
        { fr: 'Configuration routeurs et switches', en: 'Router and switch configuration' },
        { fr: 'Sécurisation réseau', en: 'Network security' },
        { fr: 'Optimisation performances', en: 'Performance optimization' }
      ]
    },
    {
      icon: Database,
      title: { fr: 'Sauvegardes & Archivage', en: 'Backup & Archiving' },
      description: { fr: 'Protection et sauvegarde de vos données critiques', en: 'Protection and backup of your critical data' },
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8',
      features: [
        { fr: 'Solutions de sauvegarde automatiques', en: 'Automatic backup solutions' },
        { fr: 'Sauvegarde cloud et locale', en: 'Cloud and local backup' },
        { fr: 'Plan de reprise d\'activité', en: 'Disaster recovery plan' },
        { fr: 'Archivage long terme', en: 'Long-term archiving' },
        { fr: 'Tests de restauration', en: 'Restoration testing' }
      ]
    },
    {
      icon: Shield,
      title: { fr: 'Sécurité Informatique', en: 'IT Security' },
      description: { fr: 'Protection contre les menaces et sécurisation des systèmes', en: 'Threat protection and system security' },
      image: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f',
      features: [
        { fr: 'Pare-feu et antivirus', en: 'Firewall and antivirus' },
        { fr: 'Détection d\'intrusion', en: 'Intrusion detection' },
        { fr: 'Gestion des accès', en: 'Access management' },
        { fr: 'Audits de sécurité', en: 'Security audits' },
        { fr: 'Formation sensibilisation', en: 'Awareness training' }
      ]
    },
    {
      icon: Cloud,
      title: { fr: 'Cloud & Virtualisation', en: 'Cloud & Virtualization' },
      description: { fr: 'Solutions cloud privé et virtualisation de serveurs', en: 'Private cloud solutions and server virtualization' },
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa',
      features: [
        { fr: 'Migration vers le cloud', en: 'Cloud migration' },
        { fr: 'Virtualisation serveurs', en: 'Server virtualization' },
        { fr: 'Infrastructure as a Service', en: 'Infrastructure as a Service' },
        { fr: 'Gestion multi-cloud', en: 'Multi-cloud management' },
        { fr: 'Optimisation des coûts', en: 'Cost optimization' }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-32 overflow-hidden" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1460925895917-afdab827c52f)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <Code className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Solutions Numériques & SI' : 'Digital Solutions & IS'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {language === 'fr' ? 'Transformation digitale et systèmes d\'information pour votre entreprise' : 'Digital transformation and information systems for your business'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Services Numériques' : 'Our Digital Services'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                  <div className="relative h-64 overflow-hidden">
                    <img src={service.image} alt={service.title[language]} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-7 w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title[language]}</h3>
                    <p className="text-gray-600 mb-6">{service.description[language]}</p>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{feature[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Projet de Digitalisation ?' : 'Digitalization Project?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr' ? 'Nos experts analysent vos besoins et conçoivent la solution idéale' : 'Our experts analyze your needs and design the ideal solution'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Demander un Audit' : 'Request an Audit'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default DigitalSolutions;
